<template>
    <AuthenticatedShell full-bleed-brand>
        <div class="ea-config-layout mp-layout">
            <div class="ea-config-scroll mp-scroll">
                <div class="wizard mp-wizard">
                    <header class="mp-hero">
                        <div class="mp-hero-copy">
                            <h1 class="mp-title">Módulos para a sua conta</h1>
                            <p class="mp-lead">
                                Explore modelos de agentes por categoria e ative os que quiser usar na Escolar.Ai. Os dados
                                abaixo são <strong>demonstração</strong> até a API de catálogo existir.
                            </p>
                        </div>
                        <div class="mp-hero-stats">
                            <span class="mp-stat"
                                ><strong>{{ attachedCount }}</strong> ativos na conta</span
                            >
                            <router-link class="mp-link-all" to="/agentes">Ver agentes da escola</router-link>
                        </div>
                    </header>

                    <div class="mp-toolbar">
                        <span class="mp-toolbar-label">Categoria</span>
                        <div class="mp-chips" role="tablist" aria-label="Filtrar por categoria">
                            <button
                                v-for="cat in categories"
                                :key="cat.id"
                                type="button"
                                class="mp-chip"
                                :class="{ 'mp-chip--active': activeCategory === cat.id }"
                                role="tab"
                                :aria-selected="activeCategory === cat.id"
                                @click="activeCategory = cat.id"
                            >
                                {{ cat.label }}
                            </button>
                        </div>
                    </div>

                    <div class="mp-grid">
                        <article
                            v-for="item in filteredTemplates"
                            :key="item.id"
                            class="mp-card"
                            :class="{ 'mp-card--attached': isAttached(item.id) }"
                        >
                            <div class="mp-card-top">
                                <span class="mp-badge" :data-tone="toneFor(item)">{{ categoryLabel(item.categoryId) }}</span>
                                <span v-if="item.popular" class="mp-pill">Popular</span>
                            </div>
                            <h2 class="mp-card-title">{{ item.name }}</h2>
                            <p class="mp-card-desc">{{ item.description }}</p>
                            <ul class="mp-tags" aria-label="Etiquetas">
                                <li v-for="tag in item.tags" :key="tag" class="mp-tag">{{ tag }}</li>
                            </ul>
                            <div class="mp-card-footer">
                                <span class="mp-card-id">{{ item.shortLabel }}</span>
                                <button
                                    v-if="!isAttached(item.id)"
                                    type="button"
                                    class="mp-btn mp-btn--primary"
                                    @click="toggleAttach(item.id)"
                                >
                                    Ativar na minha conta
                                </button>
                                <button v-else type="button" class="mp-btn mp-btn--ghost" @click="toggleAttach(item.id)">
                                    Remover da conta
                                </button>
                            </div>
                        </article>
                    </div>
                </div>
            </div>
        </div>
    </AuthenticatedShell>
</template>

<script>
import AuthenticatedShell from '@/components/layout/AuthenticatedShell.vue';
import { mapState, mapActions } from 'vuex';
import { MARKETPLACE_CATEGORIES, templatesByCategory } from '@/utils/marketplaceMock';

export default {
    name: 'EscoModulosMarketplace',
    components: { AuthenticatedShell },
    data() {
        return {
            categories: MARKETPLACE_CATEGORIES,
            activeCategory: 'all'
        };
    },
    computed: {
        ...mapState(['marketplaceAttachedTemplateIds']),
        filteredTemplates() {
            return templatesByCategory(this.activeCategory);
        },
        attachedCount() {
            return (this.marketplaceAttachedTemplateIds || []).length;
        }
    },
    methods: {
        ...mapActions(['storeMarketplaceToggleTemplate']),
        isAttached(id) {
            return (this.marketplaceAttachedTemplateIds || []).includes(id);
        },
        toggleAttach(templateId) {
            this.storeMarketplaceToggleTemplate({ templateId });
        },
        categoryLabel(categoryId) {
            const c = this.categories.find(x => x.id === categoryId);
            return c ? c.label : categoryId;
        },
        toneFor(item) {
            const map = {
                academic: 'mint',
                comms: 'blue',
                admin: 'amber',
                hr: 'cyan'
            };
            return map[item.categoryId] || 'slate';
        }
    }
};
</script>

<style lang="scss" scoped>
/* Paleta alinhada à tela principal (acentos sage, menos verde-lima) */
$mp-accent: #b4c4a8;
$mp-accent-muted: #9daa92;
$mp-accent-border: rgba(180, 196, 168, 0.38);
$mp-accent-fill: rgba(180, 196, 168, 0.1);
$mp-accent-fill-hover: rgba(180, 196, 168, 0.16);
$mp-accent-text: #d8e4cc;
$mp-surface-elevated: #1e1f24;
$mp-muted: #a3aab8;
$mp-card: #18191d;
$mp-line: rgba(255, 255, 255, 0.06);

.mp-layout {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
}

.mp-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    padding: 0.5rem clamp(0.75rem, 2.5vw, 1.5rem) 1.5rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
}
.mp-scroll::-webkit-scrollbar {
    width: 0;
    height: 0;
}

.mp-wizard {
    width: 100%;
    max-width: none;
}

.mp-hero {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.mp-title {
    margin: 0 0 0.4rem;
    font-size: clamp(1.35rem, 2.4vw, 1.75rem);
    font-weight: 600;
    color: #e8eaef;
    letter-spacing: -0.02em;
}

.mp-lead {
    margin: 0;
    max-width: 42rem;
    color: $mp-muted;
    font-size: 14px;
    line-height: 1.55;
}

.mp-lead :deep(strong) {
    color: #b8c4aa;
    font-weight: 600;
}

.mp-hero-stats {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
}

.mp-stat {
    font-size: 13px;
    color: $mp-muted;
}
.mp-stat strong {
    color: $mp-accent;
    font-weight: 600;
}

.mp-link-all {
    font-size: 13px;
    font-weight: 600;
    color: $mp-accent;
    text-decoration: none;
    padding: 0.35rem 0.65rem;
    border-radius: 999px;
    border: 1px solid $mp-accent-border;
    background: rgba(180, 196, 168, 0.09);
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.mp-link-all:hover {
    background: rgba(180, 196, 168, 0.14);
    border-color: rgba(180, 196, 168, 0.5);
    color: #c8d4bc;
}

.mp-toolbar {
    margin-bottom: 1rem;
}

.mp-toolbar-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #8b939e;
    margin-bottom: 0.5rem;
}

.mp-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
}

.mp-chip {
    padding: 0.45rem 1rem;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.035);
    color: #b8c0cc;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.mp-chip:hover {
    border-color: rgba(255, 255, 255, 0.12);
    color: #e8eaef;
}

.mp-chip--active {
    color: #eceef2;
    border-color: rgba(139, 145, 133, 0.38);
    background: rgba(139, 145, 133, 0.14);
}

.mp-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 0.9rem;
    width: 100%;
}

.mp-card {
    display: flex;
    flex-direction: column;
    padding: 1.05rem 1.1rem 0.85rem;
    border-radius: 14px;
    border: 1px solid $mp-line;
    background: linear-gradient(165deg, $mp-surface-elevated 0%, $mp-card 100%);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.mp-card:hover {
    border-color: rgba(180, 196, 168, 0.22);
}

.mp-card--attached {
    border-color: $mp-accent-border;
    box-shadow: 0 0 0 1px rgba(180, 196, 168, 0.12);
}

.mp-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.65rem;
}

.mp-badge {
    display: inline-block;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    border: 1px solid transparent;
}
.mp-badge[data-tone='blue'] {
    color: #9ecae8;
    background: rgba(120, 155, 185, 0.14);
    border-color: rgba(120, 155, 185, 0.28);
}
.mp-badge[data-tone='mint'] {
    color: #a8c9b4;
    background: rgba(120, 165, 140, 0.12);
    border-color: rgba(120, 165, 140, 0.24);
}
.mp-badge[data-tone='amber'] {
    color: #d8c9a4;
    background: rgba(200, 175, 120, 0.12);
    border-color: rgba(200, 175, 120, 0.26);
}
.mp-badge[data-tone='cyan'] {
    color: #9cc8cf;
    background: rgba(110, 165, 175, 0.12);
    border-color: rgba(110, 165, 175, 0.24);
}
.mp-badge[data-tone='slate'] {
    color: #b8c0cd;
    background: rgba(130, 145, 168, 0.12);
    border-color: rgba(130, 145, 168, 0.22);
}

.mp-pill {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $mp-accent-muted;
    padding: 0.2rem 0.45rem;
    border-radius: 6px;
    background: $mp-accent-fill;
    border: 1px solid $mp-accent-border;
}

.mp-card-title {
    margin: 0 0 0.45rem;
    font-size: 1rem;
    font-weight: 600;
    color: #eceef2;
    line-height: 1.3;
}

.mp-card-desc {
    margin: 0 0 auto;
    font-size: 13px;
    line-height: 1.45;
    color: $mp-muted;
    flex: 1 1 auto;
}

.mp-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    list-style: none;
    margin: 0.75rem 0 0;
    padding: 0;
}

.mp-tag {
    font-size: 11px;
    color: #a8b0bc;
    padding: 0.2rem 0.45rem;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.mp-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 0.65rem;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.mp-card-id {
    font-size: 11px;
    font-weight: 600;
    color: #6f7784;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.mp-btn {
    padding: 0.4rem 0.85rem;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    white-space: nowrap;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.mp-btn--primary {
    background: $mp-accent-fill-hover;
    border-color: rgba(180, 196, 168, 0.42);
    color: $mp-accent-text;
}
.mp-btn--primary:hover {
    background: rgba(180, 196, 168, 0.24);
    border-color: rgba(180, 196, 168, 0.52);
}

.mp-btn--ghost {
    background: transparent;
    border-color: rgba(255, 255, 255, 0.1);
    color: #b8c0cc;
}
.mp-btn--ghost:hover {
    border-color: rgba(255, 255, 255, 0.18);
    color: #eceef2;
}
</style>
